<template>
  <!--
    A tab on the right edge of the window rather than another control in the
    top bar. The assistant is not a page you visit, it is something you pull
    out while you are in the middle of something else — and the top bar was
    getting crowded enough that it competed with the work.

    Teleported to the body so it is anchored to the window, not to whatever
    layout happens to be around it.
  -->
  <Teleport to="body">
    <Transition name="tab-slide">
      <button
        v-if="!store.open"
        type="button"
        class="tab general-assistant-tab"
        :title="`${t('assistant.title')} (Ctrl+J)`"
        :aria-label="t('assistant.open')"
        :aria-expanded="false"
        @click="store.toggle()"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9z" />
          <path d="M18 15l.9 2.1L21 18l-2.1.9L18 21l-.9-2.1L15 18l2.1-.9z" />
        </svg>
        <span class="tab__label">{{ t('assistant.short') }}</span>
      </button>
    </Transition>
  </Teleport>
</template>

<script setup>
import { onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

import { useAssistantStore } from '../store'

const { t } = useI18n()
const store = useAssistantStore()

// Ctrl+J, next to the Ctrl+K of the search box: the two things you reach for
// without leaving the keyboard.
function onKeydown(event) {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'j') {
    event.preventDefault()
    store.toggle()
  }
}

onMounted(() => document.addEventListener('keydown', onKeydown))
onUnmounted(() => document.removeEventListener('keydown', onKeydown))
</script>

<style scoped>
/*
  It sits on top of every screen in the ERP, all day, and it is not what anyone
  came to do. So it waits in the margin: the same grey as the rest of the
  furniture, faded, thin. The brand colour and the full contrast are what it
  answers with when you point at it — a handle, not a call to action.

  It used to be a brand-coloured outline with a shadow that filled solid on
  hover, which on a screen already carrying a red Anular button read as a
  second primary action fighting the first.
*/
.tab {
  position: fixed;
  right: 0;
  top: 50%;
  transform: translate(0, -50%);
  z-index: 130;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);

  padding: var(--space-3) 5px;
  border: 1px solid var(--color-border);
  border-right: none;
  border-radius: var(--radius) 0 0 var(--radius);
  background: var(--color-surface);
  color: var(--color-text-soft);
  box-shadow: var(--shadow-sm);
  opacity: 0.7;
  transition: opacity var(--fast), color var(--fast), border-color var(--fast),
              box-shadow var(--fast), transform var(--fast);
}
.tab:hover,
.tab:focus-visible {
  opacity: 1;
  color: var(--color-primary);
  border-color: var(--color-primary-ring);
  box-shadow: var(--shadow);
  /* Leans out of the edge by two pixels. Enough to feel like it moved, not
     enough to shift anything around it. */
  transform: translate(-2px, -50%);
}

/* Con el dedo, 44 de ancho: pegada al borde derecho de la pantalla, que es
   justo donde un toque se va fuera. El relleno crece, no el texto. */
@media (pointer: coarse) {
  .tab { min-width: 44px; min-height: 44px; padding-inline: var(--space-2); }
}


/* Read bottom-to-top up the edge, the way a bookmark tab does. */
.tab__label {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

@media (max-width: 700px) {
  .tab__label { display: none; }
  .tab { padding: var(--space-2) 5px; }
}

/*
  En el teléfono, un botón flotante redondo abajo a la derecha, como el de
  cualquier app. La pestaña a media altura del borde tapaba el final de los
  campos y de las filas justo donde se escribe y se toca (los códigos de la
  lista de emplazamientos, el área de texto del parte). Abajo y por encima de
  los pies de acciones (Continuar, Guardar: ~72 px), que es donde el pulgar
  llega y donde no hay nada que leer.
*/
@media (max-width: 700px) and (pointer: coarse) {
  .tab {
    top: auto;
    right: 16px;
    bottom: calc(88px + env(safe-area-inset-bottom));
    transform: none;
    width: 52px; height: 52px;
    padding: 0;
    justify-content: center;
    border: 1px solid var(--color-border);
    border-radius: 50%;
    opacity: 0.92;
    color: var(--color-primary);
    box-shadow: var(--shadow-lg);
  }
  .tab svg { width: 22px; height: 22px; }
  .tab:hover,
  .tab:focus-visible { transform: none; }
  .tab:active { transform: scale(0.94); }
  .tab-slide-enter-from,
  .tab-slide-leave-to { transform: scale(0.6); opacity: 0; }
}

/* En el teléfono el asistente se abre desde la barra de arriba, junto a la
   campana (AppLayout `.topbar__phone`): el botón flotante sobra y tapaba
   controles del parte y de las fichas. Mismo corte de 640px que la barra.
   Dentro del bloque con scope: fuera de él perdía contra `.tab[data-v]`. */
@media (max-width: 640px) {
  .tab { display: none; }
}

.tab-slide-enter-active,
.tab-slide-leave-active { transition: transform var(--base), opacity var(--fast); }
.tab-slide-enter-from,
.tab-slide-leave-to { transform: translate(100%, -50%); opacity: 0; }
</style>

<!--
  Unscoped on purpose, and only this rule. A screen that brings its own
  assistant — the quote editor has a whole column and, on a phone, a tab of its
  own — marks the body and this tab steps aside; otherwise it sat on the right
  edge ON TOP of that screen's fields, covering the end of every line of text
  on a phone. The mark is generic: the platform does not learn the names of
  the screens that use it.

  Not `:global(body.x) .tab` inside the scoped block: Vue compiles that to
  `body.x { … }` and hides the whole page. It did.
-->
<style>
body.assistant-tab-off .general-assistant-tab { display: none; }
/* Con el teclado fuera no flota sobre lo que se escribe (ui-kit utils/viewport.js). */
html.keyboard-open .general-assistant-tab { display: none; }
</style>
